// useNow — wall-clock-aligned 1s ticks shared by relative-time surfaces.
// Exact timestamps use the wall-second phase; second-quantized chain timestamps
// use the conditional-midpoint phase. A store/timer exists only while its phase
// has subscribers.
//
// Two properties matter, and a naive clock breaks both:
//
//  • Drift. setInterval(1000) drifts and coalesces (timer throttling, long
//    frames), so `now` can advance ~2000ms across one tick and a floor()-based
//    age visibly skips a second ("1s" → "3s"). Instead each phase re-arms a
//    setTimeout chain from the calibrated server clock. Integer-second chain
//    timestamps dequantized by the conditional midpoint cross floor-age buckets
//    at server .500, not .000; the exact-timestamp store stays on server .000.
//    Drift cannot accumulate because every delay is re-derived after each tick.
//
//  • Staleness. The tick is a module-level store, so it stops when the last
//    subscriber unmounts. Serving that frozen `now` to the next subscriber would
//    date a just-born New Pairs row against a clock from minutes ago, so the
//    store re-reads Date.now() whenever it starts. getSnapshot itself stays pure
//    (React requires a cached snapshot).
//
// One store, not one timer per component: only the components that call the hook
// re-render each tick (useSyncExternalStore), so the age counters keep counting
// WITHOUT re-rendering the whole table (avatars, sparklines) every second, which
// is what caused the reorder flicker.

import { useSyncExternalStore } from 'react'
import { getServerClockOffsetMs, subscribeServerClock } from '../lib/serverClock'

export type NowClockPhase = 'wall' | 'midpoint'

interface ClockStore {
  getSnapshot: () => number
  subscribe: (onChange: () => void) => () => void
}

function createClockStore(phaseMs: 0 | 500): ClockStore {
  let now = Date.now()
  const listeners = new Set<() => void>()
  let timer: ReturnType<typeof setTimeout> | null = null
  let unsubscribeClock: (() => void) | null = null

  function arm(): void {
    timer = setTimeout(() => {
      now = Date.now()
      arm()
      for (const listener of listeners) listener()
    }, delayUntilServerPhase(phaseMs))
  }

  function onServerClockChange(): void {
    // Calibration can move the server phase without moving Date.now(). Re-arm
    // immediately so a +/− offset cannot leave DOM labels on the old boundary.
    now = Date.now()
    if (timer !== null) {
      clearTimeout(timer)
      arm()
    }
    for (const listener of listeners) listener()
  }

  return {
    getSnapshot: () => now + getServerClockOffsetMs(),
    subscribe: (onChange) => {
      listeners.add(onChange)
      if (listeners.size === 1) {
        // The store froze when the last subscriber left — never serve that value.
        now = Date.now()
        unsubscribeClock = subscribeServerClock(onServerClockChange)
        arm()
      }
      return () => {
        listeners.delete(onChange)
        if (listeners.size === 0) {
          if (timer !== null) clearTimeout(timer)
          timer = null
          unsubscribeClock?.()
          unsubscribeClock = null
        }
      }
    },
  }
}

function delayUntilServerPhase(phaseMs: 0 | 500): number {
  const serverNow = Date.now() + getServerClockOffsetMs()
  const remainderMs = ((serverNow % 1000) + 1000) % 1000
  const untilPhaseMs = phaseMs - remainderMs
  // Land just after a boundary when timer precision leaves less than one frame.
  const candidateMs = untilPhaseMs > 0 ? untilPhaseMs : untilPhaseMs + 1000
  return Math.max(16, Math.ceil(candidateMs))
}

const wallClock = createClockStore(0)
const midpointClock = createClockStore(500)

/** Milliseconds-now, re-rendering once per second on the requested server phase. */
export function useNowMs(phase: NowClockPhase = 'wall'): number {
  const clock = phase === 'midpoint' ? midpointClock : wallClock
  return useSyncExternalStore(clock.subscribe, clock.getSnapshot, clock.getSnapshot)
}

/** Unix-seconds variant for consumers that window/filter by seconds. */
export function useNowSec(): number {
  return Math.floor(useNowMs() / 1000)
}
